import React, { useContext } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Id } from '../../../../convex/_generated/dataModel';
import { Button } from '../../../../catalyst-components/button';
import { Badge } from '../../../../catalyst-components/badge';
import { Select } from '../../../../catalyst-components/select';
import { Field, Label } from '../../../../catalyst-components/fieldset';
import { Spinner } from '../../../../catalyst-components/spinner';
import { ConfirmationDialog } from '../../../../catalyst-components/confirmation-dialog';
import { BaseContext } from '../../../../shared/base-context';
import {
  ArrowLeftIcon,
  TrashIcon,
  BugAntIcon,
  LightBulbIcon,
  ChatBubbleLeftIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';

type FeedbackStatus = 'submitted' | 'acknowledged' | 'in_progress' | 'resolved';
type FeedbackType = 'bug' | 'feature_request' | 'general';

const statusColors: Record<FeedbackStatus, "yellow" | "blue" | "purple" | "green"> = {
  submitted: 'yellow',
  acknowledged: 'blue',
  in_progress: 'purple',
  resolved: 'green',
};

const typeIcons: Record<FeedbackType, React.ReactNode> = {
  bug: <BugAntIcon className="h-5 w-5" />,
  feature_request: <LightBulbIcon className="h-5 w-5" />,
  general: <ChatBubbleLeftIcon className="h-5 w-5" />,
};

export const FeedbackDetail: React.FC = () => {
  const { feedbackId } = useParams<{ feedbackId: string }>();
  const navigate = useNavigate();
  const { convexUserId } = useContext(BaseContext);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const feedback = useQuery(
    api.feedback.getById,
    feedbackId ? { id: feedbackId as Id<"feedback"> } : "skip"
  );
  const updateStatus = useMutation(api.feedback.updateStatus);
  const deleteFeedback = useMutation(api.feedback.remove);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleStatusChange = async (newStatus: FeedbackStatus) => {
    if (!feedbackId) return;
    await updateStatus({
      id: feedbackId as Id<"feedback">,
      status: newStatus,
      resolved_by: newStatus === 'resolved' ? convexUserId as Id<"users"> : undefined,
    });
  };

  const handleDelete = async () => {
    if (!feedbackId) return;
    await deleteFeedback({ id: feedbackId as Id<"feedback"> });
    navigate('/admin/feedback');
  };

  if (feedback === undefined) {
    return (
      <div className="min-h-screen bg-background pt-24">
        <div className="border-b border-border/50 bg-gradient-to-r from-amber-50 to-amber-100/50 px-6 py-8">
          <div>
            <Button color="secondary" className="rounded-full" onClick={() => navigate('/admin/feedback')}>
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Feedback
            </Button>
          </div>
        </div>
        <div className="flex justify-center items-center h-64">
          <Spinner />
        </div>
      </div>
    );
  }

  if (feedback === null) {
    return (
      <div className="min-h-screen bg-background pt-24">
        <div className="border-b border-border/50 bg-gradient-to-r from-amber-50 to-amber-100/50 px-6 py-8">
          <div>
            <Button color="secondary" className="rounded-full" onClick={() => navigate('/admin/feedback')}>
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Feedback
            </Button>
          </div>
        </div>
        <div className="text-center py-12 text-muted-foreground">
          Feedback not found.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24">
      {/* Header */}
      <div className="border-b border-border/50 bg-gradient-to-r from-amber-50 to-amber-100/50 px-6 py-8">
        <div>
          <div className="flex items-center gap-4 mb-4">
            <Button color="secondary" className="rounded-full" onClick={() => navigate('/admin/feedback')}>
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Feedback
            </Button>
          </div>
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-white border-2 border-amber-200">
              {typeIcons[feedback.type as FeedbackType] || <ChatBubbleLeftIcon className="h-8 w-8 text-primary" />}
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-foreground mb-1">{feedback.subject}</h1>
              <p className="text-base text-muted-foreground">
                Feedback from {feedback.user_name || 'Unknown'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-8">
        <div className="space-y-6">
          {/* Status & Actions Bar */}
          <div className="flex items-center justify-between flex-wrap gap-4 p-4 bg-card border-2 border-border/50 rounded-xl">
            <div className="flex items-center gap-4">
              <Badge color={statusColors[feedback.status as FeedbackStatus]}>
                {feedback.status.replace('_', ' ')}
              </Badge>
              <Badge color="zinc">
                {feedback.type.replace('_', ' ')}
              </Badge>
            </div>
            <div className="flex items-center gap-4">
              <Field className="w-48 flex items-center gap-2">
                <Label className="shrink-0 text-sm">Status:</Label>
                <Select
                  value={feedback.status}
                  onChange={(e) => handleStatusChange(e.target.value as FeedbackStatus)}
                >
                  <option value="submitted">Submitted</option>
                  <option value="acknowledged">Acknowledged</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                </Select>
              </Field>
              <Button
                color="red"
                className="rounded-full"
                onClick={() => setConfirmDelete(true)}
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </div>
          </div>

          {/* Details */}
          <div className="bg-card border-2 border-border/50 rounded-xl p-6 space-y-6">
            <div>
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1">Submitted by</h4>
              <p className="text-foreground">
                {feedback.user_name || 'Unknown'}
                {feedback.user_email && (
                  <span className="text-sm text-muted-foreground ml-2">({feedback.user_email})</span>
                )}
              </p>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1">Subject</h4>
              <p className="text-foreground">{feedback.subject}</p>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1">Message</h4>
              <p className="text-foreground whitespace-pre-wrap">{feedback.message}</p>
            </div>

            <div className="border-t border-border/50 pt-4">
              <div className="flex gap-8 text-sm text-muted-foreground">
                <p>Submitted: {formatDate(feedback.created_at)}</p>
                <p>Last Updated: {formatDate(feedback.updated_at)}</p>
                {feedback.resolved_at && <p>Resolved: {formatDate(feedback.resolved_at)}</p>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation */}
      <ConfirmationDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Delete Feedback"
        description="Are you sure you want to delete this feedback? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isCritical={true}
      />
    </div>
  );
};
